/**
 * Hook to fetch remote component configuration from Origami.
 *
 * Fetches `GET /api/{module}/config` with the `X-Tenant-ID` header,
 * then extracts the entry keyed by `componentId`.
 *
 * Defaults (apiClient, tenant, module) are read from
 * IntegratedComponentContext; explicit options override them.
 */

import { useQuery } from '@tanstack/react-query';
import { useIntegratedComponentContext } from './IntegratedComponentContext';
import type {
  ModuleConfigResponse,
  UseRemoteComponentConfigOptions,
  UseRemoteComponentConfigResult,
} from './types';

export function useRemoteComponentConfig<T = unknown>(
  options: UseRemoteComponentConfigOptions,
): UseRemoteComponentConfigResult<T> {
  const ctx = useIntegratedComponentContext();

  const apiClient = options.apiClient ?? ctx?.apiClient;
  const tenant = options.tenant ?? ctx?.tenant;
  const module = options.module ?? ctx?.module;
  const enabled = options.enabled ?? true;

  const canFetch = Boolean(apiClient && module && enabled);

  const query = useQuery<T, Error>({
    queryKey: ['integrated-config', module, tenant],
    queryFn: async () => {
      if (!apiClient || !module) {
        throw new Error('Erreur de configuration du composant');
      }

      const response = await apiClient.get<ModuleConfigResponse>(
        `/${module}/config`,
        {
          headers: tenant ? { 'X-Tenant-ID': tenant } : undefined,
        },
      );

      const configMap = response.data?.data?.config;
      if (!configMap || !(options.componentId in configMap)) {
        throw new Error(
          `Erreur de configuration du composant (clef "${options.componentId}" introuvable)`,
        );
      }

      return configMap[options.componentId] as T;
    },
    enabled: canFetch,
  });

  return {
    config: query.data,
    isLoading: query.isLoading,
    error: query.error
      ? options.errorMessage ?? query.error.message
      : null,
  };
}
